import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import Info from '@mui/icons-material/Info';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import {
  Alert,
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  SvgIcon,
} from '@mui/material';

import DeleteDialog from 'components/common/DeleteDialog';
import PinIcon from 'mdi-react/PinIcon';
import ReplyIcon from 'mdi-react/ReplyIcon';
import ShareIcon from 'mdi-react/ShareIcon';
import EvidenceExportPasswordDialog from 'components/@admin/@ommniosign/EvidenceExportPasswordDialog';
import {
  downloadSuperadminMessageEvidenceBundle,
  getSuperadminEvidenceExportErrorMessage,
} from 'services/ommniosign/superadminEvidenceExport';
import { bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import messageHasExportableOmmnioSignEvidence from 'utils/messageHasExportableOmmnioSignEvidence';
import { I18n } from 'utils/i18n';
import channelShape from 'shapes/channel';
import hasPreviews from 'utils/hasPreviews';
import isPicture from 'utils/isPicture';
import isVideo from 'utils/isVideo';
import connector from './connector';

const AdminMenu = ({
  message,
  canDelete,
  canForward,
  canEdit,
  canPin,
  canReply,
  actions,
  messageId,
  permissionsState,
  temp = false,
  onDeleteConfirmed,
  visible = true,
  currentChannel,
  authToken: token = null,
  isSuperAdmin = false,
}) => {
  const [adminMenuEl, setAdminMenuEl] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [evidenceExportLoading, setEvidenceExportLoading] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [exportedFilename, setExportedFilename] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const { isMine } = message;

  const showOmmnioEvidenceExport = useMemo(
    () => isSuperAdmin && messageHasExportableOmmnioSignEvidence(message),
    [isSuperAdmin, message],
  );

  const shouldBeWhite = isPicture(message) || isVideo(message) || hasPreviews(message);

  useEffect(() => {
    if (
      messageId &&
      permissionsState === null &&
      typeof canEdit === 'undefined' &&
      !temp &&
      messageId.length === 24
    ) {
      actions.messages.permissions(messageId);
    }
  }, [actions.messages, canEdit, messageId, permissionsState, temp]);

  const openAdminMenu = event => {
    setAdminMenuEl(event.currentTarget);
  };

  const closeAdminMenu = () => {
    setAdminMenuEl(null);
  };

  const infoClicked = () => {
    actions.drawers.employeeList.open(I18n.t('EmployeeListDrawer.messageInfo'), message);
    closeAdminMenu();
  };

  const replyClicked = () => {
    actions.chatPanel.reply({ message, currentChannel });
    closeAdminMenu();
  };

  const forwardClicked = () => {
    actions.chatPanel.forward({ messages: [message], currentChannel });
    closeAdminMenu();
  };

  const editClicked = () => {
    actions.chatPanel.edit({ message, currentChannel });
    closeAdminMenu();
  };

  const pinClicked = async () => {
    await actions.messages.pin(message);
  };

  const deleteClicked = () => {
    setShowDeleteDialog(true);
    closeAdminMenu();
  };

  const deleteConfirmed = () => {
    setShowDeleteDialog(false);
    closeAdminMenu();
    if (onDeleteConfirmed) {
      onDeleteConfirmed();
    }
  };

  const deleteCanceled = () => {
    setShowDeleteDialog(false);
    closeAdminMenu();
  };

  const evidenceExportClicked = async () => {
    if (!token?.hash) {
      setSnackbarMessage(I18n.t('AdminEvidenceExportScene.NoSession'));
      closeAdminMenu();
      return;
    }
    const mid = message?._id || messageId;
    if (!mid) {
      setSnackbarMessage(I18n.t('AdminEvidenceExportScene.ErrorGeneric'));
      closeAdminMenu();
      return;
    }
    closeAdminMenu();
    setEvidenceExportLoading(true);
    try {
      const { filename, password } = await downloadSuperadminMessageEvidenceBundle(
        String(mid),
        token.hash,
      );
      setExportedFilename(filename);
      setExportPassword(password);
      setPasswordDialogOpen(true);
    } catch (e) {
      setSnackbarMessage(getSuperadminEvidenceExportErrorMessage(e));
    } finally {
      setEvidenceExportLoading(false);
    }
  };

  if (permissionsState !== 'loaded' && typeof canEdit === 'undefined') {
    return null;
  }
  return (
    <Box sx={{ position: 'absolute', right: '7px', top: '3px', zIndex: 1 }}>
      <Box sx={{ position: 'relative' }}>
        {shouldBeWhite && isMine ? (
          <Box
            sx={{
              transform: visible ? 'translateX(0)' : 'translateX(20px)',
              transition: 'all 0.1s ease 0.1s',
              opacity: visible ? 1 : 0,
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,.8)',
              filter: 'blur(16px)',
            }}
          />
        ) : null}
        <Button
          onClick={openAdminMenu}
          sx={theme => ({
            minWidth: 0,
            minHeight: 0,
            fontSize: '1em',
            padding: 0,
            transform: visible ? 'translateX(0)' : 'translateX(20px)',
            transition: 'all 0.1s ease 0.1s',
            opacity: visible ? 1 : 0,
            borderRadius: '17px',
            // palette.white flips to black in dark mode; this arrow stays white.
            color:
              shouldBeWhite && isMine ? theme.palette.common.white : theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          })}
        >
          <KeyboardArrowDown />
        </Button>
        <Menu
          id="admin-edit-menu"
          anchorEl={adminMenuEl}
          onClose={closeAdminMenu}
          open={Boolean(adminMenuEl)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: -24, horizontal: 'left' }}
        >
          <MenuItem onClick={infoClicked}>
            <ListItemIcon>
              <Info />
            </ListItemIcon>
            {I18n.t('AdminMenu.Info')}
          </MenuItem>
          {showOmmnioEvidenceExport ? (
            <MenuItem onClick={evidenceExportClicked} disabled={evidenceExportLoading}>
              <ListItemIcon>
                <FileDownloadOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={I18n.t('AdminMenu.ExportEncryptedEvidence')} />
            </MenuItem>
          ) : null}
          {canReply ? (
            <MenuItem onClick={replyClicked}>
              <ListItemIcon>
                <SvgIcon>
                  <ReplyIcon />
                </SvgIcon>
              </ListItemIcon>
              {I18n.t('AdminMenu.Reply')}
            </MenuItem>
          ) : null}
          {canPin ? (
            <MenuItem onClick={pinClicked}>
              <ListItemIcon>
                <SvgIcon>
                  <PinIcon />
                </SvgIcon>
              </ListItemIcon>
              {I18n.t('AdminMenu.Pin')}
            </MenuItem>
          ) : null}
          {canEdit ? (
            <MenuItem onClick={editClicked}>
              <ListItemIcon>
                <Edit />
              </ListItemIcon>
              {I18n.t('AdminMenu.Edit')}
            </MenuItem>
          ) : null}
          {canForward ? (
            <MenuItem onClick={forwardClicked}>
              <ListItemIcon>
                <SvgIcon>
                  <ShareIcon />
                </SvgIcon>
              </ListItemIcon>
              {I18n.t('AdminMenu.Forward')}
            </MenuItem>
          ) : null}
          {canDelete ? (
            <MenuItem onClick={deleteClicked}>
              <ListItemIcon>
                <Delete />
              </ListItemIcon>
              {I18n.t('AdminMenu.Delete')}
            </MenuItem>
          ) : null}
        </Menu>
        <DeleteDialog
          message={I18n.t('AdminMenu.Are you sure')}
          isOpen={showDeleteDialog}
          onConfirm={deleteConfirmed}
          onClose={deleteCanceled}
        />
        <EvidenceExportPasswordDialog
          open={passwordDialogOpen}
          onClose={() => {
            setPasswordDialogOpen(false);
            setExportPassword('');
            setExportedFilename('');
          }}
          password={exportPassword}
          filename={exportedFilename}
        />
        <Snackbar
          open={Boolean(snackbarMessage)}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarMessage(null)}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

AdminMenu.propTypes = {
  message: object.isRequired,
  canEdit: bool.isRequired,
  canForward: bool.isRequired,
  canDelete: bool.isRequired,
  canPin: bool.isRequired,
  canReply: bool.isRequired,
  onDeleteConfirmed: func.isRequired,
  visible: bool,
  actions: object.isRequired,
  messageId: string.isRequired,
  permissionsState: string.isRequired,
  temp: bool,
  currentChannel: channelShape.isRequired,
  authToken: object,
  isSuperAdmin: bool,
};

export default connector(AdminMenu);
